import React from "react";
import { Mail, Zap, Cpu, Wrench, Users } from "lucide-react";
import Seo from "@/components/Seo";
import PhotoBackdrop from "@/components/PhotoBackdrop";
import Breadcrumb from "@/components/Breadcrumb";
import CTABanner from "@/components/CTABanner";
import AboutRelated from "@/components/AboutRelated";
import { COMPANY } from "@/data/company";

// Compliance-safe careers page:
// - NO invented job openings, salaries, benefits, holiday counts or headcount growth.
// - Uses the standard industry phrase: "Open roles will be advertised here from
//   time to time. If none are currently listed we still welcome CVs on file."
// - Discipline chips describe the type of engineer we tend to hire — not
//   specific vacancies.

const DISCIPLINES = [
  {
    icon: Zap,
    title: "Electrical Engineers",
    body: "Time-served industrial electricians and 18th Edition qualified engineers comfortable across commercial and process environments.",
  },
  {
    icon: Cpu,
    title: "Controls / PLC Engineers",
    body: "Programmers and commissioning engineers working with mainstream PLC, HMI and SCADA platforms.",
  },
  {
    icon: Wrench,
    title: "Mechanical Engineers",
    body: "Hands-on fitters and mechanical technicians for hydraulics, pneumatics, water systems, welding and fabrication.",
  },
  {
    icon: Users,
    title: "Operations / Coordination",
    body: "Planners, service coordinators and administrators who keep engineers moving and clients informed.",
  },
];

const CareersPage = () => (
  <div data-testid="about-careers-page">
    <Seo
      title="Careers"
      description="Careers at Lutrom Engineering — an industrial M&E firm based in Northwich, Cheshire. Open roles are advertised here from time to time. CVs from qualified engineers are always welcome."
      path="/about/careers"
    />

    <section className="relative overflow-hidden">
      <PhotoBackdrop src="/gallery/engineer.jpg" position="center 35%" />
      <div className="max-w-7xl mx-auto px-5 lg:px-10 pt-20 lg:pt-28 pb-14 lg:pb-20 relative">
        <p className="eyebrow">About · Careers</p>
        <h1
          className="font-display mt-6 text-white"
          style={{ fontSize: "clamp(40px, 5.5vw, 76px)", lineHeight: 0.98 }}
          data-testid="careers-heading"
        >
          COME BUILD
          <br />
          <span className="gradient-blue-text">WITH US.</span>
        </h1>
        <p className="mt-6 max-w-3xl text-[16px] leading-relaxed text-[color:var(--lt-text-dim)]">
          We hire time-served engineers who care about doing the job properly —
          people who&apos;d fix it on their own machine the same way they fix it on
          yours. If that sounds like you, we&apos;d like to hear from you.
        </p>
      </div>
    </section>

    <Breadcrumb items={[{ label: "About", to: "/about" }, { label: "Careers" }]} />

    {/* -------- Current openings -------- */}
    <section className="max-w-7xl mx-auto px-5 lg:px-10 mt-16" data-testid="current-openings">
      <p className="eyebrow">Open roles</p>
      <h2
        className="font-display mt-4 text-white"
        style={{ fontSize: "clamp(28px, 3.6vw, 44px)", lineHeight: 1 }}
      >
        CURRENT OPPORTUNITIES.
      </h2>

      <div
        className="glass-card rounded-2xl p-8 lg:p-10 mt-8"
        data-testid="careers-no-openings"
      >
        <p className="text-[15px] leading-relaxed text-[color:var(--lt-text-dim)]">
          Open roles will be advertised on this page from time to time. There
          are none listed at the moment — but we still welcome CVs on file from
          qualified engineers across the disciplines below. When something opens
          up, our shortlist starts here.
        </p>
        <a
          href={`${COMPANY.email.mailto}?subject=CV%20on%20file%20-%20Careers`}
          className="btn-primary inline-flex items-center gap-2 rounded-md px-5 py-3 text-sm font-semibold mt-6"
          data-testid="careers-cv-cta"
        >
          <Mail size={15} />
          Send us your CV
        </a>
      </div>
    </section>

    {/* -------- The kind of people we hire -------- */}
    <section className="max-w-7xl mx-auto px-5 lg:px-10 mt-20">
      <p className="eyebrow">Who we tend to hire</p>
      <h2
        className="font-display mt-4 text-white"
        style={{ fontSize: "clamp(26px, 3.2vw, 40px)", lineHeight: 1 }}
      >
        DISCIPLINES WE&apos;RE ALWAYS INTERESTED IN.
      </h2>
      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4">
        {DISCIPLINES.map((d) => {
          const Icon = d.icon;
          return (
            <div
              key={d.title}
              className="service-card rounded-xl p-6"
              data-testid={`careers-discipline-${d.title.replace(/[^a-z]+/gi, "-").toLowerCase()}`}
            >
              <span className="icon-tile" style={{ width: 44, height: 44 }}>
                <Icon size={20} />
              </span>
              <h3
                className="font-display mt-5 text-white"
                style={{ fontSize: 19, letterSpacing: "0.02em" }}
              >
                {d.title.toUpperCase()}
              </h3>
              <p className="mt-3 text-[13.5px] text-[color:var(--lt-text-dim)] leading-relaxed">
                {d.body}
              </p>
            </div>
          );
        })}
      </div>
    </section>

    {/* -------- Life at Lutrom -------- */}
    <section className="max-w-7xl mx-auto px-5 lg:px-10 mt-20 grid grid-cols-1 lg:grid-cols-12 gap-10">
      <div className="lg:col-span-5">
        <p className="eyebrow">Life at Lutrom</p>
        <h2
          className="font-display mt-4 text-white"
          style={{ fontSize: "clamp(26px, 3.2vw, 40px)", lineHeight: 1 }}
        >
          SMALL FIRM.
          <br />
          <span className="gradient-blue-text">SERIOUS WORK.</span>
        </h2>
      </div>
      <div className="lg:col-span-7 space-y-5 text-[15px] leading-relaxed text-[color:var(--lt-text-dim)]">
        <p>
          Lutrom is a growing engineering firm — we&apos;re not a national
          contractor and we don&apos;t pretend to be. That means you&apos;ll know
          the people you work with, own the accounts you look after, and see
          the work you do actually make a difference for the client.
        </p>
        <p>
          Our base is Unit 28, Winnington Business Park in Northwich, with
          engineer patches across Cheshire, Greater Manchester, Merseyside and
          Lancashire. Site work is the norm; workshop work supports it.
        </p>
        <p>
          Specific salary, package, working pattern and progression details are
          discussed at interview stage on a role-by-role basis.
        </p>
      </div>
    </section>

    <CTABanner
      variant="standard"
      title="Think we&#39;d fit? Send us a CV."
      body={`Email your CV to ${COMPANY.email.display}. Everything sent to us for careers is held on file and treated in confidence.`}
      phoneLocation="about_careers_cta"
    />

    <AboutRelated excludeSlug="careers" />
  </div>
);

export default CareersPage;
